/**
 * RÉFÉRENTIELS → CATÉGORIES DE FRAIS — la liste qui classe les frais réglés en espèces (maquette, onglet `frais`).
 *
 * ⚠️ LA LISTE EST LA SORTE `EXPENSE_CATEGORY_KIND` DE `reference_list`, écrite par le producteur : elle descend aux caisses,
 *    qui la proposeront au geste « Frais magasin ». La note du bas dit qu'elles ne le font pas encore.
 * ⚠️ LA CORBEILLE N'EST OFFERTE QU'À UNE CATÉGORIE QU'AUCUN FRAIS NE CITE (`usages`, le compte de la garde du producteur) :
 *    ailleurs l'écran dit pourquoi elle reste, au lieu d'offrir un geste que le serveur refuse.
 * ⚠️ PAS DE « RYTHME DE VENTILATION » (le dessin en propose un) : aucun module ne le lit.
 */
import { useCallback, useEffect, useState } from 'react';
import { checkLabel, confirm, showToast } from '@orgazone/ui-web';
import { EXPENSE_CATEGORY_KIND } from '@orgazone/sync-contract';
import { api, type ReferentialExpenseCategory } from '../api';
import { t } from '../i18n';
import { FacetError, FacetReadFailure, reasonOf } from './referential-facets-shared';

export function ExpenseCategoriesTab({ onWritten }: {
  /** Ce qu'il faut relire après une écriture : la colonne qui compte la liste. */
  onWritten: () => void;
}) {
  const [rows, setRows] = useState<ReferentialExpenseCategory[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(() => {
    api.referentielExpenseCategories()
      .then((r) => { setRows(r.rows); setFailure(null); })
      // ⚠️ L'ÉCHEC SE DIT : une liste vide se lirait « ce magasin n'a aucune catégorie », et quelqu'un les recréerait.
      .catch((e: unknown) => { setRows(null); setFailure(reasonOf(e)); });
  }, []);
  useEffect(read, [read]);

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!rows) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;

  /** Ajoute une catégorie en fin de liste ; le libellé saisi sert de code, comme pour les autres listes du magasin. */
  const create = async () => {
    const v = draft.replace(/\s+/g, ' ').trim();
    if (!v) return;
    const verdict = checkLabel(v, rows.map((r) => r.label));
    if (verdict.etat === 'exact') { setError(t('La catégorie « {v} » existe déjà.', { v: verdict.candidat })); return; }
    setBusy(true); setError(null);
    try {
      await api.createMasterDataOnly('reference_list', { kind: EXPENSE_CATEGORY_KIND, code: v, label: v, sortOrder: rows.length + 1 },
        `reference_list:${EXPENSE_CATEGORY_KIND}:create:${v}`);
      setDraft('');
      showToast(t('Catégorie ajoutée · {v}', { v }));
      onWritten();
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    read();
  };

  const remove = async (r: ReferentialExpenseCategory) => {
    const ok = await confirm({
      title: t('Supprimer la catégorie « {v} » ?', { v: r.label }), message: t('Aucun frais ne la cite — suppression sans impact.'),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.deleteReferentielEntry('reference_list', { kind: EXPENSE_CATEGORY_KIND, code: r.code },
        `reference_list:${EXPENSE_CATEGORY_KIND}:delete:${r.code}`);
      showToast(t('Catégorie supprimée · {v}', { v: r.label }), 'info');
      onWritten();
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    read();
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 170, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Catégories de frais')}</span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Frais généraux et frais sur achats — ventilés en comptabilité')}</span>
        </div>
        <div style={{ display: 'flex', gap: 9, padding: '13px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
          <input value={draft} aria-label={t('Nouvelle catégorie — ex. Emballages')} placeholder={t('Nouvelle catégorie — ex. Emballages')}
            onChange={(e) => { setDraft(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void create(); } }}
            className="oz-ref-input"
            style={{
              flex: 1, minWidth: 0, height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', fontSize: 'var(--t-title)', color: 'var(--ink-900)', outline: 'none',
            }} />
          <button type="button" disabled={busy} onClick={() => void create()} className="oz-ref-add"
            style={{
              height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)',
              fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer',
            }}>{t('Ajouter')}</button>
        </div>
        <FacetError message={error} />
        {rows.length === 0 && (
          <p style={{ margin: 0, padding: '16px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('La liste est vide.')}</p>
        )}
        {rows.map((r) => (
          <div key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', minHeight: 52, borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ flex: 1, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>
              {r.label}
              {!r.isActive && <span style={{ marginLeft: 8, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-400)' }}>{t('désactivée')}</span>}
            </span>
            {r.usages === 0 ? (
              <button type="button" aria-label={t('Supprimer la catégorie « {v} »', { v: r.label })} disabled={busy} onClick={() => void remove(r)}
                className="oz-ref-trash"
                style={{
                  width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'default' : 'pointer',
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" /></svg>
              </button>
            ) : (
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>
                {t('{n} frais classé — non supprimable', { n: r.usages })}</span>
            )}
          </div>
        ))}
      </div>
      <p style={{ margin: '12px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        {t('Semées selon le corps de métier à la création de la base (Console). La caisse les proposera au geste « Frais magasin » avec sa prochaine version ; d’ici là, un frais du tiroir n’a pas de catégorie.')}
      </p>
    </div>
  );
}
